import { useCallback, useEffect, useState } from "react";
import { MapPin, QrCode } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { pingNow } from "@/lib/tracking";

function ConsentDialog({ text, onAnswer }) {
  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="max-w-sm [&>button]:hidden" data-testid="tracking-consent-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-[#61a229]" /> Geolocalizzazione</DialogTitle>
          <DialogDescription className="whitespace-pre-line text-left text-sm" data-testid="tracking-consent-text">{text}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1 rounded-full" onClick={() => onAnswer(false)} data-testid="tracking-consent-decline">Non accetto</Button>
          <Button className="flex-1 rounded-full bg-[#61a229] hover:bg-[#528a22]" onClick={() => onAnswer(true)} data-testid="tracking-consent-accept">Accetto</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TrackingAgent() {
  const [st, setSt] = useState(null);
  const { pathname } = useLocation();
  const load = useCallback(() => api.get("/tech/tracking/status").then((r) => setSt(r.data)).catch(() => {}), []);

  useEffect(() => { load(); }, [pathname, load]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const active = st?.consented && st?.active_now;
  useEffect(() => {
    if (!active) return undefined;
    const tick = () => { if (document.visibilityState === "visible") pingNow("auto"); };
    tick();
    const t = setInterval(tick, st.interval_min * 60 * 1000);
    document.addEventListener("visibilitychange", tick);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", tick); };
  }, [active, st?.interval_min]);

  const answer = async (accept) => {
    await api.post("/tech/tracking/consent", { accept });
    load();
  };

  if (!st?.enabled) return null;
  if (st.consent_required) return <ConsentDialog text={st.consent_text} onAnswer={answer} />;
  if (!st.consented) {
    return (
      <button onClick={() => setSt({ ...st, consent_required: true })} data-testid="tracking-enable-banner"
        className="mb-4 flex w-full items-center gap-2 rounded-xl border border-dashed px-3 py-2 text-left text-xs text-slate-500">
        <MapPin className="h-4 w-4 text-slate-400" /> Geolocalizzazione non attiva. Tocca per rivedere il consenso.
      </button>
    );
  }
  return active ? (
    <p className="mb-4 flex items-center gap-1.5 text-[11px] text-slate-500" data-testid="tracking-active-indicator">
      <span className="h-2 w-2 animate-pulse rounded-full bg-[#61a229]" /> In servizio · posizione condivisa (orario {st.schedule.start}–{st.schedule.end})
    </p>
  ) : !st.clocked_in && !window.location.pathname.endsWith("/timbra") ? (
    <Link to="/tecnico/timbra" className="mb-4 flex items-center gap-2 rounded-xl border border-[#61a229]/40 bg-[#a2ca6b]/10 px-3 py-2 text-xs font-semibold text-[#3d6b16]" data-testid="tracking-punch-banner">
      <QrCode className="h-4 w-4" /> Non hai ancora timbrato l'entrata. Tocca per timbrare.
    </Link>
  ) : null;
}
